import { createSelector } from "@reduxjs/toolkit";
import {
  selectLanguageSearchQuery,
  selectQuickFilter,
  selectRecentLanguages,
  selectSelectedLanguages,
} from "./filterSelectors";
import { languageMetadata } from "../../lib/language-metadata";
import { doubleMetaphone } from "https://esm.sh/double-metaphone@2";
export const selectLanguages = (state) => state.languages;
export const selectLanguagesData = (state) => state.languages.languages;
export const selectLanguagesLoading = (state) => state.languages.loading;
export const selectLanguagesError = (state) => state.languages.error;

export const selectPurchasedLanguages = createSelector(
  [selectLanguagesData, (state) => state.hasPaid.purchasedBookIds, (state) => state.books.books],
  (languages, purchasedBookIds, books) => {
    // Extract unique language IDs from books that have been purchased
    const purchasedLanguageIds = new Set(
      books
        .filter((book) => purchasedBookIds.includes(book.id))
        .map((book) => book.language_id)
    );

    return languages.filter((language) =>
      purchasedLanguageIds.has(language.id)
    );
  }
);

// Filtered Languages Selector
export const selectFilteredLanguages = createSelector(
  [
    selectLanguagesData,
    selectLanguageSearchQuery,
    selectQuickFilter,
    selectSelectedLanguages,
    selectRecentLanguages,
    selectPurchasedLanguages,
  ],
  (
    languages,
    searchQuery,
    quickFilter,
    selectedLanguages,
    recentLanguages,
    purchasedLanguages
  ) => {
    let filtered = languages;

    if (quickFilter === "audio") {
      filtered = filtered.filter((language) =>
        language.name.toLowerCase().includes("audio")
      );
    } else if (quickFilter === "purchased") {
      const purchasedSet = new Set(purchasedLanguages.map((lang) => lang.id));
      filtered = filtered.filter((language) => purchasedSet.has(language.id));
    } else if (quickFilter === "recent") {
      const recentSet = new Set(recentLanguages);
      filtered = filtered.filter((language) => recentSet.has(language.name));
    }

    if (!searchQuery) return filtered;

    const normalizeText = (value) =>
      value
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9\s]/g, " ")
        .replace(/\s+/g, " ")
        .trim();

    const levenshtein = (a, b) => {
      if (a === b) return 0;
      if (!a || !b) return (a || b).length;
      const dp = Array.from({ length: b.length + 1 }, () => []);
      for (let i = 0; i <= b.length; i += 1) dp[i][0] = i;
      for (let j = 0; j <= a.length; j += 1) dp[0][j] = j;
      for (let i = 1; i <= b.length; i += 1) {
        for (let j = 1; j <= a.length; j += 1) {
          const cost = a[j - 1] === b[i - 1] ? 0 : 1;
          dp[i][j] = Math.min(
            dp[i - 1][j] + 1,
            dp[i][j - 1] + 1,
            dp[i - 1][j - 1] + cost
          );
        }
      }
      return dp[b.length][a.length];
    };

    const isLooseMatch = (query, target) => {
      if (!query || !target) return false;
      if (target.includes(query) || query.includes(target)) return true;
      if (query.length >= 6 && target.length >= 6) {
        return levenshtein(query, target) <= 2;
      }
      return false;
    };

    const normalizedQuery = normalizeText(searchQuery);
    const [searchPhonetic] = doubleMetaphone(normalizedQuery);

    return filtered.filter((language) => {
      const nameNormalized = normalizeText(language.name);
      const [languagePhonetic] = doubleMetaphone(nameNormalized);
      const tags = (languageMetadata[language.name] || []).map(normalizeText);
      const matchesTag = tags.some((tag) => {
        if (!tag) return false;
        const [tagPhonetic] = doubleMetaphone(tag);
        return (
          isLooseMatch(normalizedQuery, tag) || tagPhonetic === searchPhonetic
        );
      });

      return (
        isLooseMatch(normalizedQuery, nameNormalized) || // Regular substring match
        languagePhonetic === searchPhonetic || // Phonetic match
        matchesTag
      );
    });
  }
);

export const selectCurrentLanguage = (selectedLang) =>
  createSelector([selectLanguagesData], (languages) =>
    languages.find((item) => item.name === selectedLang)
  );
